import { differenceInDays, eachDayOfInterval, isAfter, format } from "date-fns";
import { DateTime, DateTimeFormatOptions } from "luxon";

// Whenever we need yyyy-mm-dd, server sends UTC
export const toYYYYMMDD = (date: Date) => {
  const yyyy = date.getFullYear().toString();
  const mm = (date.getMonth() + 1).toString();
  const dd = date.getDate().toString();
  const mmChars = mm.split("");
  const ddChars = dd.split("");
  return (
    yyyy +
    "-" +
    (mmChars[1] ? mm : "0" + mmChars[0]) +
    "-" +
    (ddChars[1] ? dd : "0" + ddChars[0])
  );
};

// Whenever we need mm/dd/yyyy, server sends yyyy-mm-dd
export const fromYYYYMMDD = (yyyymmdd: string) => {
  if (yyyymmdd) {
    const match = yyyymmdd.match(/(\d+)-(\d+)-(\d+)/);
    if (match) {
      return `${match[2]}/${match[3]}/${match[1]}`;
    }
    return yyyymmdd;
  }
  return "";
};

export const yyyymmddToDate = (yyyymmdd?: string): Date | undefined => {
  if (yyyymmdd) {
    const match = yyyymmdd.match(/(\d+)-(\d+)-(\d+)/);
    if (match) {
      const [yyyy, mm, dd] = match.slice(1);
      const d = new Date();
      d.setFullYear(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
      return d;
    }
  }
  return undefined;
};

export const dateToAge = (birthDate?: Date): number => {
  if (birthDate) {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }
  return 0;
};

interface Created {
  created_at?: string;
}

export function mostRecentlyCreated<T extends Created>(
  array: T[] | undefined
): T | undefined {
  return array
    ? array.reduce((acc: T | undefined, e: T) => {
        if (
          !acc ||
          new Date(e.created_at || "").getTime() >
            new Date(acc.created_at || "").getTime()
        ) {
          return e;
        }
        return acc;
      }, undefined)
    : undefined;
}

export const hoursAgo = (date: number) => {
  const now = Number(new Date());
  let hours = Math.floor(Math.abs(date - now) / 36e5);
  return `${hours} hours ago`;
};

export const dddd_MMM_DD_hh_mm = (date: Date) => {
  return date.toLocaleString("en-US", {
    weekday: "short",
    day: "numeric",
    year: "numeric",
    month: "short",
    hour: "numeric",
    minute: "numeric",
  });
};

export interface daysAgoWithMessage {
  days?: number;
  message: string;
}

export const daysAgoForDateReceived = (start: Date): daysAgoWithMessage => {
  const now = new Date();
  try {
    const days = eachDayOfInterval({ start, end: now }).length - 1;
    return {
      days: days,
      message: `${days} day${days === 1 ? "" : "s"} ago`,
    };
  } catch (e) {
    if (e instanceof RangeError) {
      // Sort of an 'abandon ship' moment, but because we have a lot
      // of time zone issues, let's try to at least show _something_ useful
      // This might commonly happen with a date in the future
      if (start > now) {
        const days = differenceInDays(start, now);
        return {
          days: days,
          message: `${days} day${days === 1 ? "" : "s"} from now`,
        };
      } else {
        return {
          days: undefined,
          message: `Date Error`,
        };
      }
    }
    return {
      days: undefined,
      message: "",
    };
  }
};

export const getSlideRetentionDate = (date: Date, today = new Date()) => {
  if (isAfter(date, today)) {
    const daysDifference = differenceInDays(date, today);
    return daysDifference >= 1
      ? `Available for ${daysDifference} day${daysDifference === 1 ? "" : "s"}`
      : "Available for today";
  } else {
    return `Archived ${format(date, "MM/dd/yyyy")}`;
  }
};

export const formatForDatetimeInput = (date: Date) =>
  format(date, "yyyy-MM-dd'T'HH:mm");

// For use with Luxon
// Matches the format accepted by datetime-local input
// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local#max
// Described by MDN as YYYY-MM-DDThh:mm
export const HTML_DATETIME_LOCAL_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

export const getDateTimefromTimestamp = (
  date: string,
  zone?: string | null
) => {
  return DateTime.fromISO(date, {
    zone: zone || DateTime.local().zone,
  });
};

export const getLocalizedStringWithZone = (
  date: DateTime,
  format: DateTimeFormatOptions,
  zone?: string | null
) => {
  return `${date.toLocaleString(format)} ${getZoneString(zone)}`;
};

// Formats a timestamp for a datetime input, if no zone is given, formats it to the local time.
export const formatTimestampForDateTime = (
  date: string,
  zone?: string | null
) => {
  return getDateTimefromTimestamp(date, zone).toFormat(
    HTML_DATETIME_LOCAL_INPUT_FORMAT
  );
};

// A simple helper function to normalize zone strings for display around the websites
export const getZoneString = (zone?: string | null): string => {
  return zone || DateTime.local().zoneName;
};

// Formats a date string into an API ready ISO timestamp
// toJSDate() is used due to the differing ways the DateTime and JS Date objects format their ISO timestamps.
//   JS Date objects format correctly for the API as opposed to DateTime.
// TODO: Explore if there is a means to use only DateTime objects here in order to create an API ready timestamp.
export const prepareDatetimeStringForAPI = (
  date: string,
  zone?: string | null
) => {
  return DateTime.fromFormat(date, HTML_DATETIME_LOCAL_INPUT_FORMAT, {
    zone: zone || DateTime.local().zone,
  }).toISO();
};

// timestamp/dateTimeToLocaleDateTimeString()
// Formats a timestamp string or a DateTime object into a string with the format "7/5/1989, 11:01:42 AM CDT"
export const timestampToLocaleDateTimeString = (
  timestamp: string,
  zone?: string | null
) => {
  return dateTimeToLocaleDateTimeString(
    getDateTimefromTimestamp(timestamp, zone)
  );
};

export const dateTimeToLocaleDateTimeString = (dateTime: DateTime) => {
  return `${dateTime.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)} ${
    dateTime.offsetNameShort
  }`;
};

// timestamp/dateTimeToLocaleDateString()
// Formats a timestamp string or a DateTime object into a string with the format "7/5/1989"
export const timestampToLocaleDateString = (
  timestamp: string,
  zone?: string | null
) => {
  return dateTimeToLocaleDateString(getDateTimefromTimestamp(timestamp, zone));
};

export const dateTimeToLocaleDateString = (dateTime: DateTime) => {
  return dateTime.toLocaleString(DateTime.DATE_SHORT);
};
